import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, MailCheck } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { BrandMark } from '@/shared/components/brand-mark'
import { useAppReady } from '@/shared/hooks/use-app-ready'
import { authRepository } from './api/auth-repository'

// Password reset request — REAL as of 2026-08-06. It previously ran a
// `setTimeout` and claimed "Revisa tu correo" without calling Firebase, so a
// player who forgot their password was locked out for good while being told
// the email was on its way.
//
// Deliberately reports success even when Firebase says `auth/user-not-found`:
// telling an anonymous visitor which addresses have accounts is an account
// enumeration leak. Only genuine failures (offline, malformed, rate-limited)
// surface an error.
export function ForgotPasswordPage() {
  useAppReady()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await authRepository.sendPasswordReset(email.trim())
      setSent(true)
    } catch (err) {
      const code = (err as { code?: string })?.code
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        // Same screen as success — don't confirm whether the address exists.
        setSent(true)
      } else {
        setError('No pudimos enviar el enlace. Revisa tu conexión e inténtalo de nuevo.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-full flex flex-col items-center justify-center px-6 safe-top safe-bottom">
      <div className="w-full max-w-sm flex flex-col items-center gap-5 um-fade">
        <span className={sent ? 'text-[color:var(--ok)]' : 'text-[color:var(--brand-mint)]'}>
          {sent ? <MailCheck size={64} /> : <BrandMark size={64} />}
        </span>
        <div className="text-center">
          <h1 className="text-[length:var(--fs-xl)] font-bold">
            {sent ? 'Revisa tu correo' : 'Recupera tu cuenta'}
          </h1>
          <p className="text-[color:var(--fg-muted)] mt-1">
            {sent
              ? `Te hemos enviado un enlace a ${email || 'tu correo'} para restablecer tu contraseña.`
              : 'Te enviaremos un enlace para restablecer tu contraseña.'}
          </p>
        </div>

        {!sent && (
          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex flex-col gap-3">
            {error && (
              <p role="alert" className="text-[length:var(--fs-sm)] text-[color:var(--bad)]">
                {error}
              </p>
            )}
            <label className="flex flex-col gap-1.5">
              <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">Correo</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="tucorreo@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="field"
              />
            </label>
            <Button type="submit" size="lg" disabled={busy} aria-busy={busy}>
              {busy ? (
                <>
                  <Loader2 size={18} className="motion-safe:animate-spin" /> Enviando…
                </>
              ) : (
                'Enviar enlace'
              )}
            </Button>
          </form>
        )}

        <Link to="/login" className="text-[color:var(--accent)] text-[length:var(--fs-sm)] min-h-[var(--tap)] inline-flex items-center">
          Volver a iniciar sesión
        </Link>
      </div>
    </div>
  )
}
